import { supabase } from './supabase';
import { callMcpTool } from './mcpClient';
// #568 — `tools.image_url` guarda la ruta del objeto; se firma al leer.
import { resolveRowsImageField } from '../utils/toolImageStorage';
import { inventoryImageOpts } from './inventoryService';

/**
 * Frontend service for the tool_requests workflow (#105).
 *
 * READs go through Supabase directly (RLS gates rows). WRITEs go
 * through MCP so audit logs fire server-side and the supervisor
 * notifications are sent from the same code path.
 */

// ============================================================
// READ — direct Supabase (RLS filters rows automatically)
// ============================================================

/**
 * Lists tool_requests visible to the caller.
 *   - technician sees only their own
 *   - supervisor / admin see all
 *
 * Joins the requester, tool, project, and requested_location so the
 * caller can render a row without extra round-trips.
 */
export const listToolRequests = async ({
  status,
  requester_id,
  tool_id,
  project_id,
  limit = 50,
  offset = 0
} = {}) => {
  try {
    let q = supabase
      .from('tool_requests')
      .select(`
        *,
        requester:profiles!requester_id(id, full_name, role),
        approver:profiles!approved_by(id, full_name),
        tool:tools!tool_id(id, name, serial_number, status, requires_approval, image_url),
        project:projects!project_id(id, project_number, title),
        requested_location:tool_locations!requested_location_id(id, code, label, type)
      `)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (status) q = q.eq('status', status);
    if (requester_id) q = q.eq('requester_id', requester_id);
    if (tool_id) q = q.eq('tool_id', tool_id);
    if (project_id) q = q.eq('project_id', project_id);

    const { data, error } = await q;
    const rows = data || [];
    // #568 — la miniatura de cada solicitud sale del join `tool.image_url`,
    // que en la base guarda la RUTA del objeto. Se firma acá, en una tanda.
    if (rows.length > 0) {
      await resolveRowsImageField(supabase, rows, {
        ...(await inventoryImageOpts()),
        field: 'tool.image_url'
      });
    }
    return { data: rows, error };
  } catch (error) {
    return { data: [], error };
  }
};

/**
 * The supervisor inbox: pending requests only, oldest first. Convenience
 * wrapper over listToolRequests — kept as a separate function so it can
 * later switch to the dedicated MCP tool if we need server-side
 * decoration (e.g. SLA timers).
 */
export const listPendingToolRequests = () =>
  listToolRequests({ status: 'pending', limit: 100 });

/**
 * Lists every tool_request created by the current authenticated user,
 * across every status. Used by the "Mis solicitudes" page so the
 * technician/leader can track their own pending/approved/rejected
 * history without paging through the supervisor inbox.
 */
export const listMyToolRequests = async ({ status, limit = 100 } = {}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: [], error: null };
    return listToolRequests({
      requester_id: user.id,
      status,
      limit
    });
  } catch (error) {
    return { data: [], error };
  }
};

/**
 * Lists the configurable tool_locations catalog (mig 059). Used by the
 * "Solicitar herramienta" modal to populate the requested_location_id
 * dropdown so the supervisor sees a structured destination instead of
 * free text. We hit Supabase directly — RLS already lets every
 * authenticated user read the catalog.
 */
export const listToolLocations = async ({ active_only = true } = {}) => {
  try {
    let q = supabase
      .from('tool_locations')
      .select('id, code, label, type, project_id, parent_location_id, is_active')
      .order('type')
      .order('label');
    if (active_only) q = q.eq('is_active', true);
    const { data, error } = await q;
    return { data: data || [], error };
  } catch (error) {
    return { data: [], error };
  }
};

// ============================================================
// WRITE — through MCP
// ============================================================

export const createToolRequest = ({ tool_id, project_id, requested_location_id, reason }) =>
  callMcpTool('create_tool_request', {
    tool_id,
    project_id: project_id || null,
    requested_location_id: requested_location_id || null,
    reason: reason || null
  });

export const approveToolRequest = ({ request_id }) =>
  callMcpTool('approve_tool_request', { request_id });

export const rejectToolRequest = ({ request_id, reason }) =>
  callMcpTool('reject_tool_request', { request_id, reason });

export const cancelToolRequest = ({ request_id }) =>
  callMcpTool('cancel_tool_request', { request_id });
